export type DocumentDraft = {
  title: string;
  markdown: string;
  links: { entityType: "customer" | "tool"; entityId: string }[];
};
export type DocumentSaveState = "saved" | "unsaved" | "saving" | "error" | "conflict";
export type DocumentSessionSnapshot = {
  draft: DocumentDraft;
  revision: number;
  generation: number;
  state: DocumentSaveState;
  message: string | null;
};

/** Serializes saves, retaining edits made during an in-flight write and stopping on conflicts. */
export class DocumentSession {
  private current: DocumentSessionSnapshot;
  private saved: string;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private attached = false;
  private node: HTMLElement | null = null;
  constructor(
    private readonly options: {
      draft: DocumentDraft;
      revision: number;
      save: (draft: DocumentDraft, revision: number) => Promise<number>;
      isConflict: (error: unknown) => boolean;
    },
  ) {
    this.current = {
      draft: options.draft,
      revision: options.revision,
      generation: 0,
      state: "saved",
      message: null,
    };
    this.saved = JSON.stringify(options.draft);
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  snapshot = () => this.current;
  private emit() {
    for (const listener of this.listeners) listener();
  }
  dirty = () => JSON.stringify(this.current.draft) !== this.saved;
  change = (patch: Partial<DocumentDraft>) => {
    const state = this.current.state;
    this.current = {
      ...this.current,
      draft: { ...this.current.draft, ...patch },
      state:
        state === "conflict" || state === "error" ? state : this.running ? "saving" : "unsaved",
    };
    if (!this.running && state !== "conflict" && !this.dirty()) {
      this.current = { ...this.current, state: "saved", message: null };
    }
    this.emit();
    if (state !== "conflict" && state !== "error") this.schedule();
  };
  private schedule() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, 900);
  }
  flush = async (): Promise<void> => {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.running || !this.dirty() || this.current.state === "conflict") return;
    if (!this.current.draft.title.trim()) {
      this.current = {
        ...this.current,
        state: "unsaved",
        message: "Give this page a title to save it.",
      };
      this.emit();
      return;
    }
    const draft = this.current.draft;
    const revision = this.current.revision;
    this.running = true;
    this.current = { ...this.current, state: "saving", message: null };
    this.emit();
    try {
      const nextRevision = await this.options.save(draft, revision);
      this.saved = JSON.stringify(draft);
      this.current = {
        ...this.current,
        revision: nextRevision,
        state: this.dirty() ? "unsaved" : "saved",
        message: null,
      };
    } catch (error) {
      this.current = {
        ...this.current,
        state: this.options.isConflict(error) ? "conflict" : "error",
        message: error instanceof Error ? error.message : "Your changes could not be saved.",
      };
    } finally {
      this.running = false;
      this.emit();
    }
    if (this.attached && this.current.state === "unsaved") this.schedule();
  };
  useSavedVersion(draft: DocumentDraft, revision: number) {
    if (this.running) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.saved = JSON.stringify(draft);
    this.current = {
      draft,
      revision,
      generation: this.current.generation + 1,
      state: "saved",
      message: null,
    };
    this.emit();
  }
  /** Serialize metadata writes with autosave without replacing the document or selection. */
  async updateMetadata(save: (revision: number) => Promise<number>) {
    await this.flush();
    if (this.running || this.dirty())
      throw new Error("Finish saving the document before updating its details.");
    this.running = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.current = { ...this.current, state: "saving" };
    this.emit();
    try {
      const revision = await save(this.current.revision);
      this.current = { ...this.current, revision, state: this.dirty() ? "unsaved" : "saved" };
    } finally {
      this.running = false;
      if (this.current.state === "saving")
        this.current = { ...this.current, state: this.dirty() ? "unsaved" : "saved" };
      this.emit();
      if (this.dirty() && this.attached) this.schedule();
    }
  }
  canLeave = () => !this.dirty() && !this.node?.querySelector("[data-document-upload-pending]");
  private beforeUnload = (event: BeforeUnloadEvent) => {
    if (this.canLeave()) return;
    event.preventDefault();
  };
  private beforeNavigate = (event: Event) => {
    if (
      !this.canLeave() &&
      !window.confirm("Your changes have not been saved. Leave this document?")
    )
      event.preventDefault();
  };
  attach = (node: HTMLElement | null) => {
    if (!node) return;
    this.attached = true;
    this.node = node;
    window.addEventListener("beforeunload", this.beforeUnload);
    window.addEventListener("das:operations:before-navigate", this.beforeNavigate);
    return () => {
      this.attached = false;
      this.node = null;
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
      window.removeEventListener("beforeunload", this.beforeUnload);
      window.removeEventListener("das:operations:before-navigate", this.beforeNavigate);
    };
  };
}
